import React, { ReactNode } from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Prose } from '@osmosis/tailwind-ui/components/Prose';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { textBlue, text3, text5 } from '@osmosis/tailwind-ui/styles/text';
import {
  bg0,
  bgPrimaryLightest,
  borderColorBlue600,
} from '@osmosis/tailwind-ui/styles';
import { faWandMagicSparkles } from '@fortawesome/pro-duotone-svg-icons';
import { useIntl } from 'react-intl';
import { TranscriptEntry } from '@osmosis/shared/video';

import OsmosisAIButton from '../../../ai/OsmosisAIButton';
import { chapterDomId, HighlightedChapter } from './transcriptHelpers';

type Props = {
  chaptersWithHighlights: HighlightedChapter[];
  activeEntry: TranscriptEntry | undefined;
  isLocked: boolean;
  entriesContainerRef: React.RefObject<HTMLDivElement | null>;
  sectionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  onTimestampClick: (entry: TranscriptEntry) => void;
  onAskAI: (text: string) => void;
  /** Floating sync pill, rendered as an overlay inside this container. */
  syncPill: ReactNode;
};

/** Chapter-grouped transcript entries: timestamp, highlighted text, Ask AI. */
export default function TranscriptEntries({
  chaptersWithHighlights,
  activeEntry,
  isLocked,
  entriesContainerRef,
  sectionRefs,
  onTimestampClick,
  onAskAI,
  syncPill,
}: Props) {
  const intl = useIntl();

  return (
    <Box
      ref={entriesContainerRef}
      data-testid="captions-entries"
      className={cn('relative flex-1 px-3', isLocked && 'overflow-hidden')}
    >
      {syncPill}

      {chaptersWithHighlights.map(({ chapter, entries }, index) => {
        const chapterRange = `${chapter.startLabel}–${chapter.endLabel}`;
        return (
          <Box
            key={chapterDomId(index)}
            id={chapterDomId(index)}
            data-chapter-index={index}
            ref={(node: HTMLDivElement | null) => {
              sectionRefs.current[index] = node;
            }}
            className="pb-4"
          >
            {chapter.title && (
              <Box className="pt-1 pb-1">
                <H4 className="flex flex-col items-start justify-start gap-1 font-bold">
                  <span className={text5}>{chapter.title}</span>
                  <span className={cn(text3)}>{chapterRange}</span>
                </H4>
              </Box>
            )}

            {entries.map(({ entry, html }) => {
              const isActiveLine = entry === activeEntry;
              return (
                <Flex
                  key={`${index}-${entry.start}`}
                  data-testid="captions-entry"
                  aria-current={isActiveLine ? 'true' : undefined}
                  className={cn(
                    'relative group gap-4 py-2 rounded-md pl-2 pr-2 items-center border-l-4',
                    isActiveLine
                      ? cn(borderColorBlue600, bgPrimaryLightest)
                      : 'border-transparent hover:bg-zinc-100',
                  )}
                >
                  <Button
                    variant="transparent"
                    data-testid="captions-entry-timestamp"
                    disabled={isLocked}
                    onClick={() => onTimestampClick(entry)}
                    aria-label={intl.formatMessage(
                      {
                        id: 'learn.captions_transcript.jump_to_time',
                        defaultMessage: 'Jump to {time}',
                      },
                      { time: entry.startLabel },
                    )}
                    cn={cn(
                      'shrink-0 !h-auto !p-0 text-sm font-bold hover:underline',
                      textBlue,
                    )}
                  >
                    {entry.startLabel}
                  </Button>
                  <Prose
                    data-testid="captions-entry-content"
                    content={html}
                    className={cn(
                      'flex-1 min-w-0 text-lg md:text-base font-bold md:font-normal',
                      text5,
                    )}
                  />
                  <Box className="absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 group-hover:flex">
                    <OsmosisAIButton
                      handleClickOverride={() => {
                        onAskAI(entry.text);
                      }}
                      leftIcon={
                        <Icon
                          icon={faWandMagicSparkles}
                          className="mr-1"
                          size={12}
                        />
                      }
                      classNames={{
                        container: cn('w-32'),
                        button: cn(
                          bg0,
                          borderColorBlue600,
                          `hover:!text-white w-32 h-[26px] rounded-full py-0.5 px-2.5 !text-blue-600 ![background-image:none] duration-200 hover:bg-blue-600 hover:!text-white hover:animate-none`,
                          '',
                        ),
                        text: cn('!text-xs !font-bold !text-inherit'),
                      }}
                      skipShowTracking
                      showLoggedOut
                      persistButtonVisible
                    />
                  </Box>
                </Flex>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
